import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { HeaderConfig, PdfLayout } from '../../../utils/api/pdfLayout';
import { defaultLayout } from './PdfPanel';

interface Props {
  layout: PdfLayout;
  onPatchLayout: (fields: Partial<PdfLayout>) => void;
}

const BANDS = ['header', 'footer'] as const;

// The header/footer cards at the top of the structure pane
const HeaderFooterControls: React.FC<Props> = ({ layout, onPatchLayout }) => {
  const [openBand, setOpenBand] = useState<'header' | 'footer' | null>(null);

  const alignField = (
    label: string,
    value: any,
    onChange: (next: any) => void,
  ) => (
    <div className='ple-field'>
      <span className='ple-field__label'>{label}</span>
      <select
        className='ple-input'
        value={value ?? 'left'}
        onChange={(e) => onChange(e.target.value as any)}
      >
        <option value='left'>Left</option>
        <option value='center'>Center</option>
        <option value='right'>Right</option>
      </select>
    </div>
  );

  return (
    <>
      {BANDS.map((band) => {
        const isHeader = band === 'header';
        const field = isHeader ? 'header_config' : 'footer_config';
        const config = layout[field] ?? defaultLayout()[field]!;
        const header = config as HeaderConfig;

        const set = (fields: Partial<HeaderConfig>) =>
          onPatchLayout({
            [field]: { ...config, ...fields },
          } as Partial<PdfLayout>);

        return (
          <div className='utb-group' key={band}>
            <div className='utb-group__header'>
              <h3
                className='utb-group__name'
                onClick={(e) => {
                  e.preventDefault();
                  setOpenBand((prev) => (prev === band ? null : band));
                }}
              >
                {isHeader ? 'Header' : 'Footer'}
              </h3>
              <span className='utb-group__actions'>
                <button
                  type='button'
                  title='Appearance'
                  className={openBand === band ? 'is-active' : undefined}
                  onClick={() =>
                    setOpenBand((prev) => (prev === band ? null : band))
                  }
                >
                  <FontAwesomeIcon icon='palette' fixedWidth />
                </button>
              </span>
            </div>

            {openBand === band && (
              <div className='utb-group__style'>
                <label className='ple-band__editor-switch'>
                  <span className='ple-toggle'>
                    <input
                      type='checkbox'
                      checked={config.enabled}
                      onChange={(e) => set({ enabled: e.target.checked })}
                    />
                    <span className='ple-toggle__track' />
                  </span>
                  <span>Show {band}</span>
                </label>

                {config.enabled && (
                  <div className='ple-band__fields'>
                    {isHeader && (
                      <>
                        <div className='ple-field'>
                          <span className='ple-field__label'>Content</span>
                          <select
                            className='ple-input'
                            value={header.mode}
                            onChange={(e) =>
                              set({
                                mode: e.target.value as HeaderConfig['mode'],
                              })
                            }
                          >
                            <option value='logo'>Logo only</option>
                            <option value='text'>Text only</option>
                            <option value='logo_and_text'>Logo + text</option>
                          </select>
                        </div>

                        {(header.mode === 'logo_and_text' ||
                          header.mode === 'text') && (
                          <div className='ple-field ple-field--grow'>
                            <span className='ple-field__label'>
                              Header text
                            </span>
                            <input
                              className='ple-input'
                              type='text'
                              placeholder='Header text'
                              value={config.text ?? ''}
                              onChange={(e) => set({ text: e.target.value })}
                            />
                          </div>
                        )}
                        {(header.mode === 'logo' ||
                          header.mode === 'logo_and_text') && (
                          <>
                            <div className='ple-field'>
                              <span className='ple-field__label'>
                                Logo width
                              </span>
                              <input
                                className='ple-input'
                                type='number'
                                placeholder='Logo width'
                                value={header.logoStyle?.width ?? ''}
                                onChange={(e) =>
                                  set({
                                    logoStyle: {
                                      ...header.logoStyle,
                                      width: e.target.value,
                                    },
                                  })
                                }
                              />
                            </div>
                            <div className='ple-field'>
                              <span className='ple-field__label'>
                                Logo height
                              </span>
                              <input
                                className='ple-input'
                                type='number'
                                placeholder='Logo height'
                                value={header.logoStyle?.height ?? ''}
                                onChange={(e) =>
                                  set({
                                    logoStyle: {
                                      ...header.logoStyle,
                                      height: e.target.value,
                                    },
                                  })
                                }
                              />
                            </div>
                          </>
                        )}
                        {header.mode === 'logo_and_text' && (
                          <div className='ple-field'>
                            <span className='ple-field__label'>
                              Alignment Type
                            </span>
                            <select
                              className='ple-input'
                              value={
                                header.isJustifySeperate
                                  ? 'separate'
                                  : 'combined'
                              }
                              onChange={(e) =>
                                set({
                                  isJustifySeperate:
                                    e.target.value === 'separate',
                                })
                              }
                            >
                              <option value='combined'>Combined</option>
                              <option value='separate'>Separate</option>
                            </select>
                          </div>
                        )}
                        {header.isJustifySeperate &&
                        header.mode === 'logo_and_text' ? (
                          <>
                            {alignField(
                              'Text alignment',
                              header.textStyle?.justifyContent,
                              (value) =>
                                set({
                                  textStyle: {
                                    ...header.textStyle,
                                    justifyContent: value,
                                  },
                                }),
                            )}
                            {alignField(
                              'Logo alignment',
                              header.logoJustifyContent,
                              (value) => set({ logoJustifyContent: value }),
                            )}
                          </>
                        ) : (
                          alignField(
                            'Alignment',
                            config.justifyContent,
                            (value) => set({ justifyContent: value }),
                          )
                        )}

                        <div className='ple-field'>
                          <span className='ple-field__label'>Show on</span>
                          <select
                            className='ple-input'
                            value={config.repeat ?? 'all'}
                            onChange={(e) =>
                              set({
                                repeat: e.target.value as 'first' | 'all',
                              })
                            }
                          >
                            <option value='all'>All pages</option>
                            <option value='first'>First page only</option>
                          </select>
                        </div>
                        <div className='ple-field'>
                          <span className='ple-field__label'>Border</span>
                          <label className='ple-toggle'>
                            <input
                              type='checkbox'
                              checked={header.border === '1px solid #333333'}
                              onChange={(e) =>
                                set({
                                  border: e.target.checked
                                    ? '1px solid #333333'
                                    : 'none',
                                })
                              }
                            />
                            <span className='ple-toggle__track' />
                          </label>
                        </div>
                      </>
                    )}
                    {!isHeader && (
                      <>
                        <div className='ple-field ple-field--grow'>
                          <span className='ple-field__label'>Footer text</span>
                          <input
                            className='ple-input'
                            type='text'
                            placeholder='Footer text'
                            value={config.text ?? ''}
                            onChange={(e) => set({ text: e.target.value })}
                          />
                        </div>
                        {alignField(
                          'Alignment',
                          config.justifyContent,
                          (value) => set({ justifyContent: value }),
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default HeaderFooterControls;
